import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Save,
  Truck,
  Users,
  DollarSign,
  Settings as SettingsIcon,
  CheckCircle,
  XCircle,
  Plus,
  Award,
  X,
  Trash2,
  FileText
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface Employee {
  id: string;
  employee_number: string;
  first_name: string;
  last_name: string;
  hourly_wage: number;
  rfid_card: string;
  status: string;
  hire_date: string;
}

interface Equipment {
  node_id: number;
  display_name: string;
  equipment_type: string;
  billing_multiplier: number;
  default_project_id?: number | null;
  state_name?: string;
  last_seen?: string;
}

interface Certification {
  id: string;
  employee_id: string;
  employee_name?: string;
  equipment_type: string;
  issued_date: string;
  expires_at: string;
  status: string;
}

interface Project {
  id: string;
  project_number: string;
  name: string;
  client_name: string;
  address: string;
  start_date: string;
  end_date: string | null;
  budget: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface InvoiceLineItem {
  description: string;
  employee_name?: string;
  equipment_name?: string;
  date: string;
  hours: number;
  rate: number;
  amount: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  project_id: string;
  project_name: string;
  client_name: string;
  billing_period_start: string;
  billing_period_end: string;
  line_items: InvoiceLineItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  status: string;
  issue_date: string;
  due_date: string;
  payment_date: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

// Equipment types for dropdown
const EQUIPMENT_TYPES = [
  'Bulldozer',
  'Excavator',
  'Backhoe',
  'Skid Steer',
  'Crane',
  'Loader',
  'Grader',
  'Compactor',
  'Forklift',
  'Dump Truck',
];

// ============================================================================
// API CLIENT
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, options);
  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Settings() {
  const [activeTab, setActiveTab] = useState<'equipment' | 'employees' | 'certifications' | 'projects' | 'invoices'>('equipment');
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [equipmentData, employeesData, certificationsData, projectsData, invoicesData] = await Promise.all([
        fetchAPI<Equipment[]>('/nodes'),
        fetchAPI<Employee[]>('/employees'),
        fetchAPI<Certification[]>('/certifications'),
        fetchAPI<Project[]>('/projects'),
        fetchAPI<Invoice[]>('/invoices'),
      ]);
      setEquipment(equipmentData);
      setEmployees(employeesData);
      setCertifications(certificationsData);
      setProjects(projectsData);
      setInvoices(invoicesData);
    } catch (err) {
      setError('Failed to load data from server. Please check your connection.');
      if (import.meta.env.DEV) {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Update equipment
  const updateEquipment = async (nodeId: number, updates: Partial<Equipment>) => {
    setSaving(nodeId.toString());
    setError(null);
    try {
      const payload = {
        display_name: updates.display_name,
        equipment_type: updates.equipment_type,
        billing_multiplier: updates.billing_multiplier,
        default_project_id: updates.default_project_id,
      };
      const result = await fetchAPI<{ status: string; node_id: number }>(`/nodes/${nodeId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (result.status === 'ok') {
        setEquipment(prev => prev.map(eq => eq.node_id === nodeId ? {
          ...eq,
          ...updates,
        } : eq));
        showSuccess('Updated equipment');
      }
    } catch (err) {
      setError('Failed to update equipment');
    } finally {
      setSaving(null);
    }
  };

  // Create equipment
  const createEquipment = async (data: Partial<Equipment>) => {
    setSaving('new');
    setError(null);
    try {
      if (typeof data.node_id !== 'number') {
        throw new Error('Node ID is required');
      }
      const payload = {
        display_name: data.display_name || `Node ${data.node_id}`,
        equipment_type: data.equipment_type || EQUIPMENT_TYPES[0],
        billing_multiplier: data.billing_multiplier || 1.0,
      };
      const result = await fetchAPI<{ status: string; node_id: number }>(`/nodes/${data.node_id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (result.status === 'ok') {
        setEquipment(prev => [
          ...prev,
          {
            node_id: data.node_id as number,
            display_name: payload.display_name,
            equipment_type: payload.equipment_type,
            billing_multiplier: payload.billing_multiplier,
          },
        ]);
        showSuccess('Created equipment');
      }
      setShowAddForm(false);
    } catch (err) {
      setError('Failed to create equipment');
    } finally {
      setSaving(null);
    }
  };

  // Update employee
  const updateEmployee = async (id: string, updates: Partial<Employee>) => {
    setSaving(id);
    setError(null);
    try {
      const updated = await fetchAPI<Employee>(`/employees/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      setEmployees(prev => prev.map(emp => emp.id === id ? updated : emp));
      showSuccess(`Updated ${updated.first_name} ${updated.last_name}`);
    } catch (err) {
      setError('Failed to update employee');
    } finally {
      setSaving(null);
    }
  };

  // Create employee
  const createEmployee = async (data: Partial<Employee>) => {
    setSaving('new');
    setError(null);
    try {
      const created = await fetchAPI<Employee>('/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      setEmployees(prev => [...prev, created]);
      showSuccess(`Created ${created.first_name} ${created.last_name}`);
      setShowAddForm(false);
    } catch (err) {
      setError('Failed to create employee');
    } finally {
      setSaving(null);
    }
  };

  // Create certification
  const createCertification = async (data: Partial<Certification>) => {
    setSaving('new');
    setError(null);
    try {
      await fetchAPI<Certification>('/certifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      await fetchData(); // Refresh all data
      showSuccess('Certification added successfully');
      setShowAddForm(false);
    } catch (err) {
      setError('Failed to create certification');
    } finally {
      setSaving(null);
    }
  };

  // Renew certification
  const renewCertification = async (id: string, newExpiryDate: string) => {
    setSaving(id);
    setError(null);
    try {
      await fetchAPI<Certification>(`/certifications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_at: newExpiryDate }),
      });
      await fetchData();
      showSuccess('Certification renewed');
    } catch (err) {
      setError('Failed to renew certification');
    } finally {
      setSaving(null);
    }
  };

  // Delete equipment
  const deleteEquipment = async () => {
    setError('Delete is not supported for nodes in this build');
  };

  // Delete employee
  const deleteEmployee = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete ${name}? This will also delete all associated certifications. This action cannot be undone.`)) {
      return;
    }

    setSaving(id);
    setError(null);
    try {
      await fetchAPI(`/employees/${id}`, {
        method: 'DELETE',
      });
      setEmployees(prev => prev.filter(emp => emp.id !== id));
      // Also remove certifications for this employee
      setCertifications(prev => prev.filter(cert => cert.employee_id !== id));
      showSuccess(`Deleted ${name}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete employee';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Delete certification
  const deleteCertification = async (id: string) => {
    if (!confirm('Are you sure you want to delete this certification? This action cannot be undone.')) {
      return;
    }

    setSaving(id);
    setError(null);
    try {
      await fetchAPI(`/certifications/${id}`, {
        method: 'DELETE',
      });
      setCertifications(prev => prev.filter(cert => cert.id !== id));
      showSuccess('Certification deleted');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete certification';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Update project
  const updateProject = async (id: string, updates: Partial<Project>) => {
    setSaving(id);
    setError(null);
    try {
      await fetchAPI<Project>(`/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      await fetchData(); // Refetch to get updated data
      showSuccess('Project updated');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update project';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Create project
  const createProject = async (data: Partial<Project>) => {
    setSaving('new');
    setError(null);
    try {
      await fetchAPI('/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      await fetchData();
      showSuccess('Project created');
      setShowAddForm(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create project';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Delete project
  const deleteProject = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      return;
    }

    setSaving(id);
    setError(null);
    try {
      await fetchAPI(`/projects/${id}`, {
        method: 'DELETE',
      });
      setProjects(prev => prev.filter(proj => proj.id !== id));
      showSuccess(`Deleted ${name}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Generate invoice
  const generateInvoice = async (data: {
    project_id: string;
    billing_period_start: string;
    billing_period_end: string;
    tax_rate: number;
    due_days: number;
    notes: string;
  }) => {
    setSaving('new');
    setError(null);
    try {
      await fetchAPI('/invoices/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      await fetchData(); // Refresh all data
      showSuccess('Invoice generated successfully');
      setShowAddForm(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate invoice';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Update invoice
  const updateInvoice = async (id: string, updates: { status?: string; payment_date?: string; notes?: string }) => {
    setSaving(id);
    setError(null);
    try {
      await fetchAPI(`/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
      showSuccess('Invoice updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update invoice';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Delete invoice
  const deleteInvoice = async (id: string, invoiceNumber: string) => {
    if (!confirm(`Are you sure you want to delete invoice ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    setSaving(id);
    setError(null);
    try {
      await fetchAPI(`/invoices/${id}`, {
        method: 'DELETE',
      });
      setInvoices(prev => prev.filter(inv => inv.id !== id));
      showSuccess(`Deleted invoice ${invoiceNumber}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete invoice';
      setError(errorMessage);
    } finally {
      setSaving(null);
    }
  };

  // Download invoice PDF
  const downloadInvoicePDF = async (id: string, invoiceNumber: string) => {
    try {
      const response = await fetch(`${API_BASE}/invoices/${id}/pdf`);
      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showSuccess('PDF downloaded successfully');
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-earth-50 via-white to-soil-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-earth-50 via-white to-soil-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </a>
              <div className="flex items-center gap-3">
                <SettingsIcon className="w-8 h-8 text-primary-600" />
                <h1 className="text-2xl font-bold text-gray-900">Settings & Management</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Notifications */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <div className="bg-danger-50 border-2 border-danger-200 rounded-lg p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-danger-700 flex-shrink-0" />
              <p className="text-danger-700 font-medium">{error}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchData();
              }}
              className="px-4 py-2 bg-danger-600 text-white rounded-lg font-medium hover:bg-danger-700 transition-colors whitespace-nowrap"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <div className="bg-success-50 border-2 border-success-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-success-700" />
            <p className="text-success-700 font-medium">{success}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
          <div className="flex border-b-2 border-gray-200">
            <button
              onClick={() => { setActiveTab('equipment'); setShowAddForm(false); }}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'equipment'
                  ? 'bg-primary-50 text-primary-700 border-b-4 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Truck className="w-5 h-5" />
              Equipment
            </button>
            <button
              onClick={() => { setActiveTab('employees'); setShowAddForm(false); }}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'employees'
                  ? 'bg-primary-50 text-primary-700 border-b-4 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Users className="w-5 h-5" />
              Employees
            </button>
            <button
              onClick={() => { setActiveTab('certifications'); setShowAddForm(false); }}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'certifications'
                  ? 'bg-primary-50 text-primary-700 border-b-4 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Award className="w-5 h-5" />
              Certifications
            </button>
            <button
              onClick={() => { setActiveTab('projects'); setShowAddForm(false); }}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'projects'
                  ? 'bg-primary-50 text-primary-700 border-b-4 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-5 h-5" />
              Projects
            </button>
            <button
              onClick={() => { setActiveTab('invoices'); setShowAddForm(false); }}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'invoices'
                  ? 'bg-primary-50 text-primary-700 border-b-4 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <DollarSign className="w-5 h-5" />
              Invoices
            </button>
          </div>

          {/* Equipment Tab */}
          {activeTab === 'equipment' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Equipment Management</h2>
                  <p className="text-gray-600">Manage equipment details and billing multipliers</p>
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-5 h-5" />
                  Add Equipment
                </button>
              </div>

              {showAddForm && (
                <AddEquipmentForm
                  onSave={createEquipment}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={saving === 'new'}
                />
              )}

              <div className="space-y-4">
                {equipment.map((eq) => (
                  <EquipmentRow
                    key={eq.node_id}
                    equipment={eq}
                    projects={projects}
                    onUpdate={updateEquipment}
                    onDelete={deleteEquipment}
                    isSaving={saving === eq.node_id.toString()}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Employees Tab */}
          {activeTab === 'employees' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Employee Management</h2>
                  <p className="text-gray-600">Manage employee details and hourly wages</p>
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700"
                >
                  <Plus className="w-5 h-5" />
                  Add Employee
                </button>
              </div>

              {showAddForm && (
                <AddEmployeeForm
                  onSave={createEmployee}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={saving === 'new'}
                />
              )}

              <div className="space-y-4">
                {employees.map((emp) => (
                  <EmployeeRow
                    key={emp.id}
                    employee={emp}
                    onUpdate={updateEmployee}
                    onDelete={deleteEmployee}
                    isSaving={saving === emp.id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Certifications Tab */}
          {activeTab === 'certifications' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Certification Management</h2>
                  <p className="text-gray-600">Track and renew employee equipment certifications</p>
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Plus className="w-5 h-5" />
                  Add Certification
                </button>
              </div>

              {showAddForm && (
                <AddCertificationForm
                  employees={employees}
                  onSave={createCertification}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={saving === 'new'}
                />
              )}

              <div className="space-y-4">
                {certifications.map((cert) => (
                  <CertificationRow
                    key={cert.id}
                    certification={cert}
                    onRenew={renewCertification}
                    onDelete={deleteCertification}
                    isSaving={saving === cert.id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Project Management</h2>
                  <p className="text-gray-600">Manage construction projects and track budgets</p>
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-5 h-5" />
                  Add Project
                </button>
              </div>

              {showAddForm && (
                <AddProjectForm
                  onSave={createProject}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={saving === 'new'}
                />
              )}

              <div className="space-y-4">
                {projects.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    onUpdate={updateProject}
                    onDelete={deleteProject}
                    isSaving={saving === project.id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Invoice Management</h2>
                  <p className="text-gray-600">Generate and manage client invoices</p>
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700"
                >
                  <Plus className="w-5 h-5" />
                  Generate Invoice
                </button>
              </div>

              {showAddForm && (
                <GenerateInvoiceForm
                  projects={projects.filter(p => p.status === 'active')}
                  onSave={generateInvoice}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={saving === 'new'}
                />
              )}

              <div className="space-y-4">
                {invoices.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No invoices yet. Generate your first invoice above.</p>
                  </div>
                ) : (
                  invoices.map((invoice) => (
                    <InvoiceRow
                      key={invoice.id}
                      invoice={invoice}
                      onUpdate={updateInvoice}
                      onDelete={deleteInvoice}
                      onDownloadPDF={downloadInvoicePDF}
                      isSaving={saving === invoice.id}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ADD EQUIPMENT FORM
// ============================================================================

function AddEquipmentForm({ onSave, onCancel, isSaving }: {
  onSave: (data: Partial<Equipment>) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    node_id: '',
    display_name: '',
    equipment_type: EQUIPMENT_TYPES[0],
    billing_multiplier: '1.0',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      node_id: parseInt(formData.node_id),
      display_name: formData.display_name,
      equipment_type: formData.equipment_type,
      billing_multiplier: parseFloat(formData.billing_multiplier),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-primary-50 rounded-lg p-6 mb-6 border-2 border-primary-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Add New Equipment</h3>
        <button type="button" onClick={onCancel} className="p-1 hover:bg-primary-100 rounded">
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Node ID</label>
          <input
            type="number"
            required
            min="1"
            value={formData.node_id}
            onChange={(e) => setFormData({ ...formData, node_id: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
          <input
            type="text"
            required
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="Bulldozer #1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Type</label>
          <select
            value={formData.equipment_type}
            onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
          >
            {EQUIPMENT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Multiplier</label>
          <input
            type="number"
            step="0.1"
            min="0.5"
            max="5"
            required
            value={formData.billing_multiplier}
            onChange={(e) => setFormData({ ...formData, billing_multiplier: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
          />
        </div>

      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="submit"
          disabled={isSaving}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Creating...' : 'Create Equipment'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// ADD EMPLOYEE FORM
// ============================================================================

function AddEmployeeForm({ onSave, onCancel, isSaving }: {
  onSave: (data: Partial<Employee>) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    employee_number: '',
    first_name: '',
    last_name: '',
    hourly_wage: '25.00',
    rfid_card: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      hourly_wage: parseFloat(formData.hourly_wage),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-success-50 rounded-lg p-6 mb-6 border-2 border-success-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Add New Employee</h3>
        <button type="button" onClick={onCancel} className="p-1 hover:bg-success-100 rounded">
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee Number</label>
          <input
            type="text"
            required
            value={formData.employee_number}
            onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
            placeholder="EMP-001"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">RFID Card Number</label>
          <input
            type="text"
            required
            value={formData.rfid_card}
            onChange={(e) => setFormData({ ...formData, rfid_card: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
            placeholder="RFID-001"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
          <input
            type="text"
            required
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
          <input
            type="text"
            required
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Wage</label>
          <input
            type="number"
            step="0.50"
            min="10"
            max="100"
            required
            value={formData.hourly_wage}
            onChange={(e) => setFormData({ ...formData, hourly_wage: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="submit"
          disabled={isSaving}
          className="px-6 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Creating...' : 'Create Employee'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// ADD CERTIFICATION FORM
// ============================================================================

function AddCertificationForm({ employees, onSave, onCancel, isSaving }: {
  employees: Employee[];
  onSave: (data: Partial<Certification>) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    employee_id: employees[0]?.id || '',
    equipment_type: EQUIPMENT_TYPES[0],
    issued_date: new Date().toISOString().split('T')[0],
    expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-purple-50 rounded-lg p-6 mb-6 border-2 border-purple-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Add New Certification</h3>
        <button type="button" onClick={onCancel} className="p-1 hover:bg-purple-100 rounded">
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          <select
            value={formData.employee_id}
            onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
          >
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name} ({emp.employee_number})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Type</label>
          <select
            value={formData.equipment_type}
            onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
          >
            {EQUIPMENT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Issued Date</label>
          <input
            type="date"
            required
            value={formData.issued_date}
            onChange={(e) => setFormData({ ...formData, issued_date: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expires On</label>
          <input
            type="date"
            required
            value={formData.expires_at}
            onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="submit"
          disabled={isSaving}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Adding...' : 'Add Certification'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// EQUIPMENT ROW
// ============================================================================

function EquipmentRow({ equipment, projects, onUpdate, onDelete, isSaving }: {
  equipment: Equipment;
  projects: Project[];
  onUpdate: (id: number, updates: Partial<Equipment>) => void;
  onDelete: () => void;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(equipment.display_name);
  const [type, setType] = useState(equipment.equipment_type);
  const [multiplier, setMultiplier] = useState(equipment.billing_multiplier.toString());
  const [projectId, setProjectId] = useState((equipment.default_project_id ?? '').toString());

  const handleSave = () => {
    const updates: Partial<Equipment> = {};
    if (name !== equipment.display_name) updates.display_name = name;
    if (type !== equipment.equipment_type) updates.equipment_type = type;
    if (parseFloat(multiplier) !== equipment.billing_multiplier) {
      updates.billing_multiplier = parseFloat(multiplier);
    }
    const newProjectId = projectId ? parseInt(projectId) : null;
    if (newProjectId !== (equipment.default_project_id ?? null)) {
      updates.default_project_id = newProjectId;
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(equipment.node_id, updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setName(equipment.display_name);
    setType(equipment.equipment_type);
    setMultiplier(equipment.billing_multiplier.toString());
    setProjectId((equipment.default_project_id ?? '').toString());
    setIsEditing(false);
  };

  const defaultProject = projects.find(p => p.id === String(equipment.default_project_id));

  return (
    <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
      <div className="flex items-center gap-4">
        <div className="bg-primary-100 p-3 rounded-lg">
          <Truck className="w-6 h-6 text-primary-700" />
        </div>

        <div className="flex-1 grid grid-cols-5 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Equipment Name</div>
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-semibold text-gray-900">{equipment.display_name}</div>
            )}
            <div className="text-xs text-gray-500 mt-1">Node {equipment.node_id}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Type</div>
            {isEditing ? (
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              >
                {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            ) : (
              <div className="font-medium text-gray-700">{equipment.equipment_type}</div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              Multiplier
            </div>
            {isEditing ? (
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="5"
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-bold text-primary-700">{equipment.billing_multiplier}x</div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Default Project</div>
            {isEditing ? (
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              >
                <option value="">None</option>
                {projects.filter(p => p.status === 'active').map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            ) : (
              <div className="font-medium text-gray-700">
                {defaultProject ? defaultProject.name : <span className="text-gray-400">None</span>}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Status</div>
            <div className="font-medium text-gray-700">{equipment.state_name || 'Unknown'}</div>
          </div>
        </div>

        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete()}
                disabled={isSaving}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EMPLOYEE ROW
// ============================================================================

function EmployeeRow({ employee, onUpdate, onDelete, isSaving }: {
  employee: Employee;
  onUpdate: (id: string, updates: Partial<Employee>) => void;
  onDelete: (id: string, name: string) => void;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState(employee.first_name);
  const [lastName, setLastName] = useState(employee.last_name);
  const [hourlyWage, setHourlyWage] = useState(employee.hourly_wage.toString());

  const handleSave = () => {
    const updates: Partial<Employee> = {};
    if (firstName !== employee.first_name) updates.first_name = firstName;
    if (lastName !== employee.last_name) updates.last_name = lastName;
    if (parseFloat(hourlyWage) !== employee.hourly_wage) {
      updates.hourly_wage = parseFloat(hourlyWage);
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(employee.id, updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFirstName(employee.first_name);
    setLastName(employee.last_name);
    setHourlyWage(employee.hourly_wage.toString());
    setIsEditing(false);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
      <div className="flex items-center gap-4">
        <div className="bg-success-100 p-3 rounded-lg">
          <Users className="w-6 h-6 text-success-700" />
        </div>

        <div className="flex-1 grid grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">First Name</div>
            {isEditing ? (
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-semibold text-gray-900">{employee.first_name}</div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Last Name</div>
            {isEditing ? (
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-semibold text-gray-900">{employee.last_name}</div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Employee #</div>
            <div className="font-medium text-gray-700">{employee.employee_number}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              Hourly Wage
            </div>
            {isEditing ? (
              <input
                type="number"
                step="0.50"
                min="10"
                max="100"
                value={hourlyWage}
                onChange={(e) => setHourlyWage(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-bold text-success-700">${employee.hourly_wage}/hr</div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(employee.id, `${employee.first_name} ${employee.last_name}`)}
                disabled={isSaving}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CERTIFICATION ROW
// ============================================================================

function CertificationRow({ certification, onRenew, onDelete, isSaving }: {
  certification: Certification;
  onRenew: (id: string, newDate: string) => void;
  onDelete: (id: string) => void;
  isSaving: boolean;
}) {
  const expiryDate = new Date(certification.expires_at);
  const now = new Date();
  const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isExpired = daysUntilExpiry < 0;
  const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry < 30;

  const handleRenew = () => {
    const newExpiryDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    onRenew(certification.id, newExpiryDate);
  };

  return (
    <div className={`rounded-lg p-4 border-2 ${
      isExpired ? 'bg-danger-50 border-danger-200' :
      isExpiringSoon ? 'bg-warning-50 border-warning-200' :
      'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${
          isExpired ? 'bg-danger-100' :
          isExpiringSoon ? 'bg-warning-100' :
          'bg-purple-100'
        }`}>
          <Award className={`w-6 h-6 ${
            isExpired ? 'text-danger-700' :
            isExpiringSoon ? 'text-warning-700' :
            'text-purple-700'
          }`} />
        </div>

        <div className="flex-1 grid grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Employee</div>
            <div className="font-semibold text-gray-900">{certification.employee_name}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Equipment Type</div>
            <div className="font-medium text-gray-700">{certification.equipment_type}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Issued</div>
            <div className="font-medium text-gray-700">
              {new Date(certification.issued_date).toLocaleDateString()}
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Expires</div>
            <div className={`font-bold ${
              isExpired ? 'text-danger-700' :
              isExpiringSoon ? 'text-warning-700' :
              'text-success-700'
            }`}>
              {new Date(certification.expires_at).toLocaleDateString()}
              <div className="text-xs font-normal mt-1">
                {isExpired ? 'EXPIRED' :
                 isExpiringSoon ? `${daysUntilExpiry} days left` :
                 `${daysUntilExpiry} days left`}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleRenew}
            disabled={isSaving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {isSaving ? 'Renewing...' : 'Renew'}
          </button>
          <button
            onClick={() => onDelete(certification.id)}
            disabled={isSaving}
            className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ADD PROJECT FORM
// ============================================================================

function AddProjectForm({ onSave, onCancel, isSaving }: {
  onSave: (data: Partial<Project>) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    project_number: "",
    name: "",
    client_name: "",
    address: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    budget: "",
    status: "active",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      budget: parseFloat(formData.budget) || 0,
      end_date: formData.end_date || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-primary-50 rounded-lg p-6 mb-6 border-2 border-primary-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Add New Project</h3>
        <button type="button" onClick={onCancel} className="p-1 hover:bg-primary-100 rounded">
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project Number *</label>
          <input
            type="text"
            required
            value={formData.project_number}
            onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="PROJ-001"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="Highway Construction"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
          <input
            type="text"
            required
            value={formData.client_name}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="ABC Construction Co."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="123 Main St, City, State"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Budget</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.budget}
            onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
            placeholder="50000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
          >
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? "Creating..." : "Create Project"}
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// PROJECT ROW
// ============================================================================

function ProjectRow({ project, onUpdate, onDelete, isSaving }: {
  project: Project;
  onUpdate: (id: string, updates: Partial<Project>) => void;
  onDelete: (id: string, name: string) => void;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(project.name);
  const [clientName, setClientName] = useState(project.client_name);
  const [budget, setBudget] = useState(project.budget.toString());
  const [status, setStatus] = useState(project.status);

  const handleSave = () => {
    const updates: Partial<Project> = {};
    if (name !== project.name) updates.name = name;
    if (clientName !== project.client_name) updates.client_name = clientName;
    if (parseFloat(budget) !== project.budget) updates.budget = parseFloat(budget);
    if (status !== project.status) updates.status = status;

    if (Object.keys(updates).length > 0) {
      onUpdate(project.id, updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setName(project.name);
    setClientName(project.client_name);
    setBudget(project.budget.toString());
    setStatus(project.status);
    setIsEditing(false);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
      <div className="flex items-center gap-4">
        <div className="bg-primary-100 p-3 rounded-lg">
          <FileText className="w-6 h-6 text-primary-700" />
        </div>

        <div className="flex-1 grid grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Project Name</div>
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-semibold text-gray-900">{project.name}</div>
            )}
            <div className="text-xs text-gray-500 mt-1">{project.project_number}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Client</div>
            {isEditing ? (
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-medium text-gray-700">{project.client_name}</div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              Budget
            </div>
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                min="0"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              />
            ) : (
              <div className="font-bold text-primary-700">\${project.budget.toLocaleString()}</div>
            )}
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Status</div>
            {isEditing ? (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
              >
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            ) : (
              <div className={`font-medium ${
                project.status === "active" ? "text-success-700" :
                project.status === "completed" ? "text-gray-600" :
                "text-warning-700"
              }`}>
                {project.status === "active" ? "Active" :
                 project.status === "on_hold" ? "On Hold" :
                 "Completed"}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(project.id, project.name)}
                disabled={isSaving}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GENERATE INVOICE FORM
// ============================================================================

function GenerateInvoiceForm({ projects, onSave, onCancel, isSaving }: {
  projects: Project[];
  onSave: (data: {
    project_id: string;
    billing_period_start: string;
    billing_period_end: string;
    tax_rate: number;
    due_days: number;
    notes: string;
  }) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    project_id: projects.length > 0 ? projects[0].id : '',
    billing_period_start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    billing_period_end: new Date().toISOString().split('T')[0],
    tax_rate: '0',
    due_days: '30',
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      tax_rate: parseFloat(formData.tax_rate),
      due_days: parseInt(formData.due_days),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-success-50 rounded-lg p-6 mb-6 border-2 border-success-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Generate New Invoice</h3>
        <button type="button" onClick={onCancel} className="p-1 hover:bg-success-100 rounded">
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            required
            value={formData.project_id}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          >
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name} - {project.client_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period Start</label>
          <input
            type="date"
            required
            value={formData.billing_period_start}
            onChange={(e) => setFormData({ ...formData, billing_period_start: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period End</label>
          <input
            type="date"
            required
            value={formData.billing_period_end}
            onChange={(e) => setFormData({ ...formData, billing_period_end: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            required
            value={formData.tax_rate}
            onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Due (Days)</label>
          <input
            type="number"
            min="1"
            max="365"
            required
            value={formData.due_days}
            onChange={(e) => setFormData({ ...formData, due_days: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
          />
        </div>

        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-success-500 focus:outline-none"
            rows={3}
            placeholder="Payment terms, special instructions, etc."
          />
        </div>
      </div>

      <div className="flex gap-3 mt-4">
        <button
          type="submit"
          disabled={isSaving || projects.length === 0}
          className="flex-1 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>Generating...</>
          ) : (
            <>
              <DollarSign className="w-5 h-5" />
              Generate Invoice
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// INVOICE ROW
// ============================================================================

function InvoiceRow({ invoice, onUpdate, onDelete, onDownloadPDF, isSaving }: {
  invoice: Invoice;
  onUpdate: (id: string, updates: { status?: string; payment_date?: string; notes?: string }) => void;
  onDelete: (id: string, invoiceNumber: string) => void;
  onDownloadPDF: (id: string, invoiceNumber: string) => void;
  isSaving: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-success-100 text-success-800 border-success-200';
      case 'sent':
        return 'bg-primary-100 text-primary-800 border-primary-200';
      case 'overdue':
        return 'bg-danger-100 text-danger-800 border-danger-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const markAsPaid = () => {
    onUpdate(invoice.id, {
      status: 'paid',
      payment_date: new Date().toISOString().split('T')[0],
    });
  };

  const markAsSent = () => {
    onUpdate(invoice.id, { status: 'sent' });
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{invoice.invoice_number}</h3>
            <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(invoice.status)}`}>
              {invoice.status.toUpperCase()}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            <p><strong>{invoice.client_name}</strong> - {invoice.project_name}</p>
            <p>Period: {invoice.billing_period_start} to {invoice.billing_period_end}</p>
            <p>Total: <strong className="text-lg text-success-700">${invoice.total_amount.toFixed(2)}</strong></p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <button
              onClick={() => onDownloadPDF(invoice.id, invoice.invoice_number)}
              disabled={isSaving}
              className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
            >
              Download PDF
            </button>

            {invoice.status === 'draft' && (
              <button
                onClick={markAsSent}
                disabled={isSaving}
                className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
              >
                Mark as Sent
              </button>
            )}

            {invoice.status !== 'paid' && (
              <button
                onClick={markAsPaid}
                disabled={isSaving}
                className="px-3 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 text-sm"
              >
                Mark as Paid
              </button>
            )}

            {invoice.status !== 'paid' && (
              <button
                onClick={() => onDelete(invoice.id, invoice.invoice_number)}
                disabled={isSaving}
                className="px-3 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-300">
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <p className="text-gray-600">Issue Date: <strong>{invoice.issue_date}</strong></p>
              <p className="text-gray-600">Due Date: <strong>{invoice.due_date}</strong></p>
              {invoice.payment_date && (
                <p className="text-gray-600">Payment Date: <strong className="text-success-700">{invoice.payment_date}</strong></p>
              )}
            </div>
            <div>
              <p className="text-gray-600">Subtotal: ${invoice.subtotal.toFixed(2)}</p>
              {invoice.tax_rate > 0 && (
                <p className="text-gray-600">Tax ({invoice.tax_rate}%): ${invoice.tax_amount.toFixed(2)}</p>
              )}
              <p className="text-gray-600 font-bold">Total: ${invoice.total_amount.toFixed(2)}</p>
            </div>
          </div>

          {invoice.notes && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 font-semibold mb-1">Notes:</p>
              <p className="text-sm text-gray-700">{invoice.notes}</p>
            </div>
          )}

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Line Items ({invoice.line_items.length}):</p>
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-right">Hours</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-200 last:border-0">
                      <td className="px-3 py-2">{item.date}</td>
                      <td className="px-3 py-2">{item.description}</td>
                      <td className="px-3 py-2 text-right">{item.hours.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">${item.rate.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-semibold">${item.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

